import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { subscriptionAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Check,
  Loader2,
  Zap,
  Crown,
  CreditCard,
  X,
} from 'lucide-react';

export default function PricingPage() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchSubscription();
    
    // Handle return from Stripe checkout
    const status = searchParams.get('subscription');
    if (status === 'success') {
      toast.success('Subscription activated! Welcome to ExpenseSnap AI Pro.');
      // Remove query params
      navigate('/pricing', { replace: true });
    } else if (status === 'cancelled') {
      toast.info('Checkout cancelled');
      navigate('/pricing', { replace: true });
    }
  }, [searchParams, navigate]);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionAPI.getStatus();
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const response = await subscriptionAPI.createCheckout(planId, window.location.origin + '/pricing');
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await subscriptionAPI.createPortal();
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open subscription portal');
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      interval: null,
      description: 'Get started with basic expense tracking',
      features: [
        { text: 'Up to 10 expenses', included: true },
        { text: 'Text & voice input', included: true },
        { text: 'Basic dashboard', included: true },
        { text: 'Receipt scanning', included: false },
        { text: 'Excel export', included: false },
      ],
      cta: 'Current Plan',
      popular: false,
    },
    {
      id: 'monthly',
      name: 'Pro Monthly',
      price: 10,
      interval: 'month',
      description: 'Full access with monthly billing',
      features: [
        { text: 'Unlimited expenses', included: true },
        { text: 'Text & voice input', included: true },
        { text: 'Full dashboard & charts', included: true },
        { text: 'Receipt scanning (AI)', included: true },
        { text: 'Excel export', included: true },
        { text: '7-day free trial', included: true },
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      id: 'annual',
      name: 'Pro Annual',
      price: 100,
      interval: 'year',
      description: 'Best value - save $20/year',
      features: [
        { text: 'Unlimited expenses', included: true },
        { text: 'Text & voice input', included: true },
        { text: 'Full dashboard & charts', included: true },
        { text: 'Receipt scanning (AI)', included: true },
        { text: 'Excel export', included: true },
        { text: '7-day free trial', included: true },
        { text: 'Save $20 vs monthly', included: true },
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
  ];

  const isCurrentPlan = (planId) => {
    if (planId === 'free' && subscription?.status === 'free') return true;
    if (subscription?.plan === planId && ['active', 'trialing'].includes(subscription?.status)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start for free, upgrade when you need more. All paid plans include a 7-day free trial.
          </p>
        </div>

        {/* Current Subscription Status */}
        {subscription && subscription.status !== 'free' && (
          <Card className="bg-primary/10 border-primary/30 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-primary" />
                  <div>
                    <p className="font-semibold">
                      {subscription.is_trial ? 'Trial Active' : 'Pro Subscription'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {subscription.is_trial && subscription.trial_ends_at
                        ? `Trial ends ${new Date(subscription.trial_ends_at).toLocaleDateString('en-CA')}`
                        : subscription.current_period_end
                          ? `Renews ${new Date(subscription.current_period_end).toLocaleDateString('en-CA')}`
                          : 'Active subscription'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  className="border-primary/30 hover:bg-primary/10"
                  data-testid="manage-subscription-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Super Admin Notice */}
        {isSuperAdmin() && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-yellow-500" />
                <p className="text-sm">
                  As Super Admin, you have unlimited access to all features regardless of subscription status.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Usage Stats for Free Tier - Hidden for Super Admin */}
        {subscription?.status === 'free' && subscription?.expense_limit && !isSuperAdmin() && (
          <Card className="bg-card border-white/10 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Free Tier Usage</p>
                  <p className="text-2xl font-bold">
                    {subscription.expense_count} / {subscription.expense_limit} expenses
                  </p>
                </div>
                <div className="w-32 h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(subscription.expense_count / subscription.expense_limit) * 100}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Super Admin Badge */}
        {isSuperAdmin() && (
          <Card className="bg-primary/10 border-primary/30 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-semibold text-primary">Super Admin</p>
                  <p className="text-sm text-muted-foreground">Unlimited access to all features</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`bg-card border-white/10 relative ${
                plan.popular ? 'ring-2 ring-primary' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                  Most Popular
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  {plan.interval && (
                    <span className="text-muted-foreground">/{plan.interval}</span>
                  )}
                </div>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className={feature.included ? '' : 'text-muted-foreground'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {plan.id === 'free' ? (
                  <Button
                    variant="outline"
                    className="w-full border-white/10"
                    disabled={isCurrentPlan('free')}
                    data-testid="free-plan-btn"
                  >
                    {isCurrentPlan('free') ? 'Current Plan' : 'Downgrade'}
                  </Button>
                ) : (
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={checkoutLoading === plan.id || isCurrentPlan(plan.id)}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    {isCurrentPlan(plan.id) ? 'Current Plan' : plan.cta}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            All prices in CAD. Cancel anytime. Questions?{' '}
            <a href="mailto:support@expensesnap.ai" className="text-primary hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
